import { BaseCollection } from '../BaseCollection'
import { Company } from './Company'
import adminApiClient from '../../api/admin-api-client'
import { EventBus } from '../../util/EventBus'
import { GridStateManager } from '../../util/GridStateManager'
/**
 * Class CompanyCollection
 *
 * @extends BaseCollection
 */

export class CompanyCollection extends BaseCollection {
  constructor(props) {
    super(props)

    this.collectionKey = 'companyId'
    this.stateManager = new GridStateManager({
      gridName: 'companyGrid',
      collection: this,
      autoInitialize: false,
    })
  }

  /**
   * @override
   *
   * Get list of companies with paging
   * https://github.com/alirezanet/Gridify#supported-filtering-operators
   * @param {number} page
   * @param {number} pageSize
   * @param {string} orderBy
   * @param {string} filter
   * @returns {Company[]}
   */
  async fetch() {
    this.isLoading = true
    this.stateManager.beforeFetch()
    try {
      const companiesResponse = await adminApiClient.getCompanies({
        Page: this.page,
        PageSize: this.pageSize,
        OrderBy: this.orderBy,
        Filter: this.filter,
      })
      this.count = companiesResponse.data.count
      this.items = companiesResponse.data.data.map((c) => new Company(c))
      this.stateManager.afterFetch()
      this.isLoading = false
      if (this.filter) {
        EventBus.$emit('restoreFocusEvent')
      }
      return this.items
    } catch (ex) {
      this.isLoading = false
      this.stateManager.handleFetchError(ex)
      throw new Error(ex.message)
    }
  }

  /**
   * @override
   */
  async fetchById(companyId) {
    this.isLoading = true
    try {
      this.isLoading = true
      const companyResponse = await adminApiClient.getCompany(companyId)
      const entityContactsResponse = await adminApiClient.getCompanyEntityContacts(companyId)
      const entityAddressesResponse = await adminApiClient.getCompanyEntityAddresses(companyId)

      // Map entity contacts to their respective fields
      const entityContact = {}
      entityContactsResponse.data.forEach((contact) => {
        switch (contact.contactTypeId) {
          case 1:
            entityContact.phone = contact.contact
            break
          case 2:
            entityContact.commercialPhone = contact.contact
            break
          case 3:
            entityContact.email = contact.contact
            break
          case 4:
            entityContact.invoicingEmail = contact.contact
            break
          case 5:
            entityContact.trainingEmail = contact.contact
            break
        }
      })

      // Map entity addresses
      const entityAddress = {}
      // Add address type mapping
      const addressTypeIdToString = {
        1: 'primaryAddress',
        2: 'secondaryAddress',
        3: 'invoicingAddress',
      }

      entityAddressesResponse.data.forEach((address) => {
        const addressTypeString = addressTypeIdToString[address.addressTypeId]
        if (addressTypeString) {
          entityAddress[addressTypeString] = {
            countryCodeId: address.countryCodeId,
            countryCode: address.countryCode,
            countryText: address.countryText,
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2,
            city: address.city,
            stateProvince: address.stateProvince,
            postCode: address.postCode,
          }
        }
      })

      // Merge the entity contact and address data with the company data
      const companyData = {
        ...companyResponse.data,
        entityContact,
        entityAddress,
      }
      this.items = [new Company(companyData)]
      this.isLoading = false
      return this.items
    } catch (ex) {
      this.isLoading = false
      throw ex
    }
  }
  /**
   * @override
   *
   * @param {Company} company
   * @returns {Promise<number>} created company id
   */
  async create(company) {
    company.allowPersonalMail = company.allowPersonalMail ? 1 : 0
    company.subdivisions = null
    company.isLoading = true
    this.isLoading = true
    try {
      let response = await adminApiClient.createCompany({ data: company })
      if (response.status === 201) {
        company.companyId = response.data

        // Entity Contact type mapping
        const contactTypes = {
          phone: { id: 1, value: company.entityContact?.phone },
          commercialPhone: { id: 2, value: company.entityContact?.commercialPhone },
          email: { id: 3, value: company.entityContact?.email },
          invoicingEmail: { id: 4, value: company.entityContact?.invoicingEmail },
          trainingEmail: { id: 5, value: company.entityContact?.trainingEmail },
        }

        // Helper function to create contact
        const createContact = async (contactTypeId, contactValue) => {
          if (contactValue) {
            const contactData = {
              contactTypeId,
              entityTypeId: 1,
              entityId: company.companyId,
              contact: contactValue,
            }
            await adminApiClient.createEntityContact(contactData)
          }
        }

        // Create all contacts
        await Promise.all(Object.values(contactTypes).map(({ id, value }) => createContact(id, value)))

        // Create entity addresses
        if (company.entityAddress) {
          // Address type mapping
          const addressTypeMap = {
            primaryAddress: 1,
            secondaryAddress: 2,
            invoicingAddress: 3,
          }

          for (const [addressTypeId, address] of Object.entries(company.entityAddress)) {
            const addressData = {
              entityTypeId: 1,
              entityId: company.companyId,
              addressTypeId: addressTypeMap[addressTypeId], // Convert string to number
              ...address,
            }
            await adminApiClient.createEntityAddress(addressData)
          }
        }

        this.items.push(company)
        this.isLoading = false
        company.isLoading = false
        return company.companyId
      } else {
        throw new Error('Failed to create company')
      }
    } catch (ex) {
      this.isLoading = false
      company.isLoading = false
      throw ex
    }
  }

  async delete(companyId) {
    this.isLoading = true
    try {
      // Get all entity contacts for the company
      const entityContactsResponse = await adminApiClient.getCompanyEntityContacts(companyId)
      // Delete all entity contacts
      await Promise.all(
        entityContactsResponse.data.map((contact) => adminApiClient.deleteEntityContact(contact.contactId)),
      )

      // Get all entity addresses for the company
      const entityAddressesResponse = await adminApiClient.getCompanyEntityAddresses(companyId)
      // Delete all entity addresses
      await Promise.all(
        entityAddressesResponse.data.map((address) => adminApiClient.deleteEntityAddress(address.addressId)),
      )
      // Delete the company itself
      await adminApiClient.deleteCompany(companyId)
      this.isLoading = false
      return true
    } catch (ex) {
      this.isLoading = false
      throw ex
    }
  }
}
